/**
 * Per-rank accent colour for the Profile page tier label (specs/profile-page AC-8).
 *
 * The 10 ranks (Rookie…Icon, see src/economy/constants/ranks.constants.ts) have no colour
 * of their own — the rank crest carries the material, and the XP ring is a fixed purple.
 * This is the one intentional new colour set the Profile design introduces: an escalating
 * bronze → silver → gold → platinum → diamond → crown palette that reads on the dark screen.
 *
 * Client-only display constant — nothing here is persisted.
 */

import { TOP_RANK_INDEX } from '../../economy/constants/ranks.constants';

/** Accent colour keyed by 1-based rank index (1 = Rookie … 10 = Icon). */
export const RANK_COLORS: Record<number, string> = {
  1: '#E8A165', // Rookie — bronze
  2: '#D9A679', // Prospect — deep bronze
  3: '#C7CCD6', // Contender — silver
  4: '#AEB8C6', // Challenger — steel silver
  5: '#FCD34D', // Veteran — gold
  6: '#F6C24B', // All-Star — deep gold
  7: '#8BE8E5', // Champion — platinum
  8: '#7DD3FC', // Superstar — diamond
  9: '#C4B5FD', // Legend — amethyst
  10: '#F8A5A5', // Icon — crown crimson
};

/** Fallback used when an index falls outside the known set (defensive; getRankColor clamps). */
export const DEFAULT_RANK_COLOR = RANK_COLORS[1];

/**
 * Accent colour for a rank index, clamped into 1..TOP_RANK_INDEX so a bad payload
 * degrades to a valid colour instead of `undefined`.
 */
export function getRankColor(index: number): string {
  if (!Number.isFinite(index)) return DEFAULT_RANK_COLOR;
  const clamped = Math.min(TOP_RANK_INDEX, Math.max(1, Math.round(index)));
  return RANK_COLORS[clamped] ?? DEFAULT_RANK_COLOR;
}
